import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Chip,
  useTheme,
  alpha
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ADVERTISEMENT_TEMPLATES } from '../../constants/advertisementTemplates';

/**
 * Displays a single advertisement banner
 */
const AdvertisementBanner = ({ advertisement, onDismiss, variant = 'banner' }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  if (!advertisement) return null;

  // Backend returns content fields nested in 'content' object
  const content = advertisement.content || {};
  const badge = content.badge || advertisement.badge;
  const headline = content.headline || advertisement.headline;
  const body = content.body || advertisement.body;
  const ctaText = content.ctaText || advertisement.ctaText;
  const ctaUrl = content.ctaUrl || advertisement.ctaUrl;

  const template = ADVERTISEMENT_TEMPLATES[advertisement.templateType] || ADVERTISEMENT_TEMPLATES.announcement;
  const accentColor = advertisement.accentColor || template.accentColor;

  const handleDismiss = () => {
    if (onDismiss) {
      onDismiss(advertisement._id || advertisement.id);
    }
  };

  const handleCtaClick = () => {
    if (ctaUrl) {
      window.open(ctaUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <Paper
      elevation={3}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderLeft: `4px solid ${accentColor}`,
        mb: 2,
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: theme.shadows[6]
        }
      }}
    >
      {/* Close button */}
      <IconButton
        size="small"
        onClick={handleDismiss}
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          zIndex: 2,
          backgroundColor: alpha(theme.palette.background.paper, 0.8),
          '&:hover': {
            backgroundColor: alpha(theme.palette.background.paper, 0.95)
          }
        }}
        aria-label={t('Dismiss')}
      >
        <CloseIcon fontSize="small" />
      </IconButton>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' } }}>
        {/* Image section */}
        {advertisement.imageUrl && (
          <Box
            sx={{
              width: { xs: '100%', sm: 200 },
              height: { xs: 150, sm: 'auto' },
              minHeight: { sm: 150 },
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <img
              src={advertisement.imageUrl}
              alt={advertisement.title}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
          </Box>
        )}

        {/* Content section */}
        <Box sx={{ flex: 1, p: 3, pr: 6 }}>
          {/* Badge */}
          {badge && (
            <Chip
              label={badge}
              size="small"
              sx={{
                backgroundColor: accentColor,
                color: (theme) => theme.palette.getContrastText(accentColor),
                mb: 1
              }}
            />
          )}

          {/* Headline */}
          <Typography
            variant="h6"
            sx={{
              color: accentColor,
              mb: 1
            }}
          >
            {headline}
          </Typography>

          {/* Body text */}
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2 }}
          >
            {body}
          </Typography>

          {/* CTA Button */}
          {ctaText && (
            <Button
              variant="contained"
              size="small"
              onClick={handleCtaClick}
              sx={{
                backgroundColor: accentColor,
                '&:hover': {
                  backgroundColor: alpha(accentColor, 0.8)
                }
              }}
            >
              {ctaText}
            </Button>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default AdvertisementBanner;
